import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Stack,
	Typography,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { createOrder } from "../../../../../../services/Admin/OrdersApi";
import OrderCustomerInfo from "./OrderCustomerInfo";
import OrderItemsForm from "./AddItemAndDrink/OrderItemsForm";



const getEmptyItem = () => ({
	product_id: "",
	drink_id: "",
	quantity: 1,
	price: "",
	note: "",
	status: "pending",
});

const getInitialState = (overrides = {}) => ({
	order_type: "takeaway",
	type: "local",
	customer_id: "",
	name: "",
	phone: "",
	phone2: "",
	phone3: "",
	email: "",
	address: "",
	address2: "",
	address3: "",
	table_id: "",
	captain_order_id: "",
	delivery_id: "",
	date: "",
items: [],  
  ...overrides,
});

const toServerDateTime = (value) => {
	if (!value) return "";
	return value.replace("T", " ") + ":00";
};

const OpenOrderDialog = ({ open, onClose, onCreated, initialOrderType, initialTableId }) => {
	const [formData, setFormData] = useState(getInitialState());
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);

	useEffect(() => {
		if (!open) return;
		const normalizedOrderType = initialOrderType || "takeaway";
		const normalizedTableId =
			normalizedOrderType === "tables" && initialTableId != null
				? String(initialTableId)
				: "";

		setFormData((prev) => ({
			...getInitialState({
				order_type: normalizedOrderType,
				table_id: normalizedTableId,
			}),
			date: prev.date || new Date().toISOString().slice(0, 16),
		}));
		setError("");
		setSuccess(false);
	}, [open, initialOrderType, initialTableId]);

	const canSubmit = useMemo(() => !loading, [loading]);

	const handleFieldChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
		setError("");
	};

	const handleItemChange = (index, field, value) => {
		setFormData((prev) => {
			const items = [...prev.items];
			items[index] = {
				...items[index],
				[field]: value,
			};
			return { ...prev, items };
		});
		setError("");
	};

	const handleAddItem = (itemData) => {
  setFormData((prev) => ({
    ...prev,
    items: [
      ...prev.items,
      {
        ...getEmptyItem(),
        ...itemData, // دي اللي جاية من الديالوج
      },
    ],
  }));
};

	const handleRemoveItem = (index) => {
		setFormData((prev) => {
			if (prev.items.length === 1) return prev;
			return {
				...prev,
				items: prev.items.filter((_, itemIndex) => itemIndex !== index),
			};
		});
	};

	const validateForm = () => {
		if (!formData.order_type) {
			setError("نوع الطلب مطلوب");
			return false;
		}

		if (!formData.name.trim()) {
			setError("اسم العميل مطلوب");
			return false;
		}

		if (!formData.phone.trim()) {
			setError("رقم الهاتف مطلوب");
			return false;
		}

		if (formData.order_type === "tables" && !String(formData.table_id || "").trim()) {
			setError("معرف الطاولة مطلوب عند اختيار طلب طاولات");
			return false;
		}

		if (!formData.date) {
			setError("التاريخ مطلوب");
			return false;
		}

		if (!Array.isArray(formData.items) || formData.items.length === 0) {
			setError("يجب إضافة صنف واحد على الأقل");
			return false;
		}

		for (let i = 0; i < formData.items.length; i += 1) {
			const item = formData.items[i];
			const hasProduct = String(item.product_id || "").trim() !== "";
			const hasDrink = String(item.drink_id || "").trim() !== "";

			if (!hasProduct && !hasDrink) {
				setError(`الصنف رقم ${i + 1}: يجب إدخال product_id أو drink_id`);
				return false;
			}

			if (!item.quantity || Number(item.quantity) <= 0) {
				setError(`الصنف رقم ${i + 1}: الكمية يجب أن تكون أكبر من 0`);
				return false;
			}

			if (item.price === "" || Number(item.price) < 0) {
				setError(`الصنف رقم ${i + 1}: السعر غير صالح`);
				return false;
			}
		}

		return true;
	};

	const buildPayload = () => {
		const normalizeNullable = (value) => {
			if (value === undefined || value === null) return null;
			const text = String(value).trim();
			return text === "" ? null : text;
		};

		return {
			order_type: formData.order_type,
			type: "local",
			customer_id:
				formData.customer_id === "" || formData.customer_id == null
					? null
					: Number(formData.customer_id),
			name: formData.name.trim(),
			phone: formData.phone.trim(),
			phone2: normalizeNullable(formData.phone2),
			phone3: normalizeNullable(formData.phone3),
			email: normalizeNullable(formData.email),
			address: normalizeNullable(formData.address),
			address2: normalizeNullable(formData.address2),
			address3: normalizeNullable(formData.address3),
			table_id:
				formData.order_type === "tables" && formData.table_id !== ""
					? Number(formData.table_id)
					: null,
			captain_order_id:
				formData.captain_order_id === "" || formData.captain_order_id == null
					? null
					: Number(formData.captain_order_id),
			delivery_id:
				formData.delivery_id === "" || formData.delivery_id == null
					? null
					: Number(formData.delivery_id),
			date: toServerDateTime(formData.date),
			items: formData.items.map((item) => ({
				product_id:
					String(item.product_id || "").trim() === ""
						? null
						: Number(item.product_id),
				drink_id:
					String(item.drink_id || "").trim() === ""
						? null
						: Number(item.drink_id),
				quantity: Number(item.quantity),
				price: Number(item.price),
				note: normalizeNullable(item.note),
				status: item.status || "pending",
			})),
		};
	};

	const handleSubmit = async () => {
		if (!validateForm()) return;

		setLoading(true);
		setError("");
		setSuccess(false);

		try {
			const payload = buildPayload();
			const response = await createOrder(payload);
			setSuccess(true);

			setTimeout(() => {
				onCreated?.(response);
				onClose?.();
			}, 1000);
		} catch (err) {
			console.error("خطأ في إنشاء الطلب:", err);
			setError(err.message || "حدث خطأ أثناء إنشاء الطلب");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={loading ? undefined : onClose}
			maxWidth="lg"
			fullWidth
			dir="rtl"
			PaperProps={{
				sx: {
					borderRadius: 3,
					overflow: "hidden",
					border: "1px solid",
					borderColor: "divider",
					background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 42%)",
				},
			}}
		>
			<DialogTitle
				sx={{
					textAlign: "right",
					py: 2,
					px: { xs: 2, md: 3 },
					borderBottom: "1px solid",
					borderColor: "divider",
					background: "linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(2,136,209,0.03) 100%)",
				}}
			>
				<Stack direction="row" spacing={1.25} alignItems="center" justifyContent="flex-start">
					<Box
						sx={{
							width: 42,
							height: 42,
							borderRadius: 2,
							display: "grid",
							placeItems: "center",
							bgcolor: "primary.main",
							color: "primary.contrastText",
						}}
					>
						<AddShoppingCartIcon fontSize="small" />
					</Box>
					<Box>
						<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
							إنشاء طلب جديد
						</Typography>
						<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.4 }}>
							أدخل بيانات العميل ثم أضف الأصناف والمشروبات المطلوبة
						</Typography>
					</Box>
				</Stack>
			</DialogTitle>

			<DialogContent sx={{ pt: 2.5, px: { xs: 2, md: 3 } }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
					<Stack direction="row" spacing={1} alignItems="center">
						<ReceiptLongIcon sx={{ color: "text.secondary", fontSize: 20 }} />
						<Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary" }}>
							بيانات الطلب
						</Typography>
					</Stack>
					<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
						عدد الأصناف: {formData.items.length}
					</Typography>
				</Stack>

				{error && (
					<Alert severity="error" variant="filled" sx={{ mb: 2, borderRadius: 2 }}>
						{error}
					</Alert>
				)}

				{success && (
					<Alert severity="success" variant="filled" sx={{ mb: 2, borderRadius: 2 }}>
						تم إنشاء الطلب بنجاح
					</Alert>
				)}

				<Box
					sx={{
						p: { xs: 1.25, md: 2 },
						borderRadius: 2.5,
						border: "1px solid",
						borderColor: "divider",
						bgcolor: "background.paper",
					}}
				>
					<OrderCustomerInfo
						formData={formData}
						handleFieldChange={handleFieldChange}
						loading={loading}
					/>
				</Box>

				<Divider sx={{ my: 2.25 }} />

				<Box
					sx={{
						p: { xs: 1.25, md: 2 },
						borderRadius: 2.5,
						border: "1px solid",
						borderColor: "divider",
						bgcolor: "background.paper",
					}}
				>
					<OrderItemsForm
						items={formData.items}
						handleItemChange={handleItemChange}
						handleAddItem={handleAddItem}
						handleRemoveItem={handleRemoveItem}
						loading={loading}
					/>
				</Box>
			</DialogContent>

			<DialogActions
				sx={{
					px: { xs: 2, md: 3 },
					py: 1.75,
					gap: 1,
					borderTop: "1px solid",
					borderColor: "divider",
					bgcolor: "rgba(248,250,252,0.7)",
				}}
			>
				<Button onClick={onClose} variant="outlined" disabled={loading} sx={{ minWidth: 110, borderRadius: 2 }}>
					إلغاء
				</Button>
				<Button onClick={handleSubmit} variant="contained" disabled={!canSubmit} sx={{ minWidth: 140, borderRadius: 2, fontWeight: 700 }}>
					{loading ? "جاري الإنشاء..." : "إنشاء الطلب"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default OpenOrderDialog;
